import {getYYYYMMddFormat} from '@di-shared/api/di/utils';
import {endDateOfMonth, parseDateWithFormat, startDateOfMonth, subDate} from '@shared/dateUtils';

export const HISTORY_FILTER_MODE = {
  FILTER: 'filter',
  RESULT: 'result',
} as const;

export type HistoryFilterMode = (typeof HISTORY_FILTER_MODE)[keyof typeof HISTORY_FILTER_MODE];

export const PERIOD = {
  SELECT_MONTH: 3,
  CURRENT_MONTH: 0,
  PREVIOUS_1_MONTH: 1,
  PREVIOUS_2_MONTH: 2,
  RECENT_7_DAYS: 7,
  RECENT_30_DAYS: 30,
  RECENT_90_DAYS: 90,
};

export const HISTORY_FILTER_PERIOD = {
  CURRENT_MONTH: 'current-month',
  PREVIOUS_1_MONTH: 'previous-1-month',
  PREVIOUS_2_MONTH: 'previous-2-month',
  RECENT_7_DAYS: 'recent-7-days',
  RECENT_30_DAYS: 'recent-30-days',
  CUSTOM_DAYS: 'custom-days',
} as const;

export type HistoryFilterPeriod = (typeof HISTORY_FILTER_PERIOD)[keyof typeof HISTORY_FILTER_PERIOD];

export interface HistoryFilterFormState {
  period: HistoryFilterPeriod;
  startDate: Date | null;
  endDate: Date | null;
  tagNames: Array<string>;
}

export interface HistoryFilterQueryState extends HistoryFilterFormState {
  sourceDate: Date | null;
}

function isValidPeriod(period: string | null): period is HistoryFilterPeriod {
  return !!period && Object.values(HISTORY_FILTER_PERIOD).includes(period as HistoryFilterPeriod);
}

export function getHistoryFilterPeriodState(period: HistoryFilterPeriod, today = new Date()) {
  switch (period) {
    case HISTORY_FILTER_PERIOD.CURRENT_MONTH:
      return {
        period,
        startDate: startDateOfMonth(today),
        endDate: today,
      };
      break;
    case HISTORY_FILTER_PERIOD.PREVIOUS_1_MONTH:
      return {
        period,
        startDate: startDateOfMonth(subDate(today, {months: PERIOD.PREVIOUS_1_MONTH})),
        endDate: endDateOfMonth(subDate(today, {months: PERIOD.PREVIOUS_1_MONTH})),
      };
      break;
    case HISTORY_FILTER_PERIOD.PREVIOUS_2_MONTH:
      return {
        period,
        startDate: startDateOfMonth(subDate(today, {months: PERIOD.PREVIOUS_2_MONTH})),
        endDate: endDateOfMonth(subDate(today, {months: PERIOD.PREVIOUS_2_MONTH})),
      };
      break;
    case HISTORY_FILTER_PERIOD.RECENT_7_DAYS:
      return {
        period,
        startDate: subDate(today, {days: PERIOD.RECENT_7_DAYS - 1}),
        endDate: today,
      };
      break;
    case HISTORY_FILTER_PERIOD.RECENT_30_DAYS:
      return {
        period,
        startDate: subDate(today, {days: PERIOD.RECENT_30_DAYS - 1}),
        endDate: today,
      };
      break;
    case HISTORY_FILTER_PERIOD.CUSTOM_DAYS:
      return {
        period,
        startDate: null,
        endDate: null,
      };
  }
}

export function getDefaultHistoryFilterState(today = new Date()): HistoryFilterQueryState {
  const defaultPeriodState = getHistoryFilterPeriodState(HISTORY_FILTER_PERIOD.RECENT_7_DAYS, today);

  return {
    ...defaultPeriodState,
    sourceDate: null,
    tagNames: [],
  };
}

export function parseHistoryFilterSearchParams(searchParams: URLSearchParams, today = new Date()): HistoryFilterQueryState {
  const defaultState = getDefaultHistoryFilterState(today);
  const period = searchParams.get('period');
  const validatedPeriod = isValidPeriod(period) ? period : defaultState.period;
  const periodState = getHistoryFilterPeriodState(validatedPeriod, today);
  const startDate = parseDateWithFormat(searchParams.get('startDate')) || periodState.startDate;
  const endDate = parseDateWithFormat(searchParams.get('endDate')) || periodState.endDate;

  return {
    sourceDate: parseDateWithFormat(searchParams.get('sourceDate')),
    period: validatedPeriod,
    startDate,
    endDate,
    tagNames: searchParams.getAll('tag').filter(Boolean),
  };
}

export function createHistoryFilterSearchParams(state: HistoryFilterQueryState) {
  const params = new URLSearchParams();

  params.set('period', state.period);

  if (state.sourceDate) {
    params.set('sourceDate', getYYYYMMddFormat(state.sourceDate));
  }

  if (state.startDate) {
    params.set('startDate', getYYYYMMddFormat(state.startDate));
  }

  if (state.endDate) {
    params.set('endDate', getYYYYMMddFormat(state.endDate));
  }

  state.tagNames.forEach((tagName) => {
    params.append('tag', tagName);
  });

  return params;
}

export function createHistoryFilterSearch(state: HistoryFilterQueryState) {
  return `?${createHistoryFilterSearchParams(state).toString()}`;
}

export function getHistoryFilterFormState(state: HistoryFilterQueryState): HistoryFilterFormState {
  return {
    period: state.period,
    startDate: state.startDate,
    endDate: state.endDate,
    tagNames: state.tagNames,
  };
}
